import type {
  CompleteMethodology,
  MethodologyDeepPayload,
  MethodologyEntity,
  PhenomenonSingle
} from '@protector/entities/methodology/models';
import { HydrateMethodologyDetail } from 'entities/methodology/methodology.actions';
import { parsePhenomenonSingleToPhenomenon } from 'entities/phenomenon/phenomenon.helper';
import { getPhenomenaByMethodologyId } from 'entities/phenomenon/phenomenon.service';
import { getSeasonsTreeByCompany } from 'entities/season/season.service';
import { getUserById } from 'entities/user/user.service';
import { captureError } from 'infra/monitoring/monitoring.utils';
import type { ActionsObservable } from 'redux-observable';
import { ofType } from 'redux-observable';
import { forkJoin, of } from 'rxjs';
import { catchError, concatMap, map, shareReplay } from 'rxjs/operators';
import type { LoadMethodologyDeep, UpdateMethodologyDeep } from './methodology-deep.actions';
import {
  LoadMethodologyDeepFailure,
  LoadMethodologyDeepSuccess,
  MethodologyDeepActionTypes,
  UpdateMethodologyDeepFailure,
  UpdateMethodologyDeepSuccess
} from './methodology-deep.actions';
import { convertToMethodologyDeep, updateMethodologyDeep, validateUserInfo } from './methodology-deep.helper';
import { getMethodologyDetailResponse, getMethodologyOnBase } from './methodology-deep.service';

export const handleLoadMethodologyDeep = (action$: ActionsObservable<ReturnType<typeof LoadMethodologyDeep>>) => {
  let methodology: CompleteMethodology;
  let methodologyDetailEntity: MethodologyEntity;
  return action$.pipe(
    ofType(MethodologyDeepActionTypes.LOAD_METHODOLOGY_DEEP),
    map(action => action.payload),
    concatMap(methodologyAction => {
      const methodologyResponse$ = getMethodologyDetailResponse(methodologyAction.id).pipe(shareReplay(1));

      return forkJoin([
        methodologyResponse$.pipe(map(response => response.data)),
        getMethodologyOnBase(methodologyAction.id, methodologyResponse$).pipe(
          catchError(() =>
            of({
              data: {
                seasonIds: [],
                seasonAreaIds: [],
                seasonPropertyIds: []
              }
            })
          )
        )
      ]);
    }),
    map(results => {
      const [methodologyDetail, base] = results;

      methodologyDetailEntity = methodologyDetail;

      methodology = {
        ...methodologyDetail,
        season_ids: base.data.seasonIds,
        season_area_ids: base.data.seasonAreaIds,
        season_property_ids: base.data.seasonPropertyIds
      };

      return methodology;
    }),
    concatMap(results => {
      return forkJoin([
        of(results),
        getPhenomenaByMethodologyId(results.id),
        getSeasonsTreeByCompany(methodology.company_id),
        getUserById(methodology.last_modified_by_user_id)
      ]);
    }),
    concatMap(([methodologyWithSeason, phenomenaByMethodology, seasonsTree, userInfo]) => {
      const phenomenonsTree = phenomenaByMethodology.data.map((phenomenon: PhenomenonSingle) =>
        parsePhenomenonSingleToPhenomenon(phenomenon)
      );

      const userName = validateUserInfo(userInfo, 'name');
      const userRole = validateUserInfo(userInfo, 'job_title');

      const methodologyDeep = convertToMethodologyDeep(methodologyWithSeason, phenomenonsTree, seasonsTree.data, userName, userRole);

      return [HydrateMethodologyDetail(methodologyDetailEntity), LoadMethodologyDeepSuccess(methodologyDeep)];
    }),
    catchError((error: string) => {
      return of(LoadMethodologyDeepFailure(error));
    })
  );
};

export const handleUpdateMethodologyDeep = (action$: ActionsObservable<ReturnType<typeof UpdateMethodologyDeep>>) => {
  return action$.pipe(
    ofType(MethodologyDeepActionTypes.UPDATE_METHODOLOGY_DEEP),
    map(action => action.payload),
    concatMap((methodologyPayload: MethodologyDeepPayload) =>
      updateMethodologyDeep(
        methodologyPayload.methodologyDeep,
        methodologyPayload.newIndicator,
        methodologyPayload.methodologyAnalyticContextDTO,
        methodologyPayload.phenomenon
      ).pipe(
        map(response => {
          return UpdateMethodologyDeepSuccess(response);
        })
      )
    ),
    catchError((error: Error) => {
      captureError(error, { errorType: 'AxiosError', domain: 'Methodologies' });
      return of(UpdateMethodologyDeepFailure(error.message));
    })
  );
};
